import { FormRule } from "antd";
import { validateEntityFormula } from "src/pages/EntityCardPage/apiEntityCard";
import { AttrTypeName } from "src/types/AttrType";
import { ZAttribute } from "src/types/ZAttribute";
import { z } from "zod";
import { zUid, ZUid } from "src/types/zUid";
import { getFormulaDependencyIds } from "src/common/formula/getFormulaDependencyIds";
import { getEditorInfo } from "../../getEditorInfo";

const zFormulaErrorInfo = z.object({
  argIds: zUid.array(),
  formulaId: zUid,
  message: z.string(),
});

export const zFormulaError = z.object({
  formulaInfo: zFormulaErrorInfo.optional(),
});

export const zFormulaArg = z.object({
  id: zUid,
  name: z.string(),
  attributeRepresentation: z.string(),
});
export type ZFormulaArg = z.infer<typeof zFormulaArg>;

enum formulaLinkType {
  objectLink = "OBJECT_ID",
}

const zArgMetaBase = z.object({
  attributeId: zUid,
});

const zObjectLinkArg = zArgMetaBase.extend({
  linkTypeName: z.literal(formulaLinkType.objectLink),
  attributeLinkToObjectId: zUid,
});

// Вероятно, в будущем будут возможны и другие варианты доп информации об аргументах
const zArgMeta = z.discriminatedUnion("linkTypeName", [zObjectLinkArg]);

export const zFormulaArgsInfo = z.object({
  availableAttributes: zFormulaArg.array(),
  availableAttributesLinks: zArgMeta.array(),
});
export type ZFormulaArgsInfo = z.infer<typeof zFormulaArgsInfo>;

export const attrFormulaPefixView = "@";
export const attrFormulaPefixBuss = "a";
export const attrLinkedFormulaPefixBuss = "o";

export const makeFormulaAttPartView = (attr: ZFormulaArg) =>
  `${attrFormulaPefixView}${attr.name}`;
export const makeFormulaAttPartBuss = (attr: ZFormulaArg) =>
  attr.attributeRepresentation;

export const parseFormulaAtts = (
  formula: string,
  { availableAttributes, availableAttributesLinks }: ZFormulaArgsInfo,
): ZUid[] => {
  // Сохраняем тип ID из API, но зависимости определяем по пути в формуле.
  const ids = new Map<string, ZUid>(
    availableAttributes.map(({ id }) => [String(id), id]),
  );
  availableAttributesLinks.forEach(({ attributeLinkToObjectId }) => {
    ids.set(String(attributeLinkToObjectId), attributeLinkToObjectId);
  });
  return getFormulaDependencyIds(formula).map((id) => ids.get(id) ?? id);
};

const hasWrongAttrPartView = (template: string, atts: ZFormulaArg[]) => {
  let formula = template;
  atts
    .map(makeFormulaAttPartView)
    .sort((a, b) => b.length - a.length)
    .forEach((part) => {
      formula = formula.replaceAll(part, "");
    });
  return (
    formula[formula.length - 1] !== attrFormulaPefixView &&
    formula.includes(attrFormulaPefixView)
  );
};

export const validateFormula = (
  objectId: ZUid,
  attrList: ZFormulaArg[],
): FormRule => ({
  validator: async (rule, value) => {
    try {
      const bussinesValue = transformFormula4Save(value, attrList);
      await validateEntityFormula(bussinesValue, objectId);
      if (hasWrongAttrPartView(value, attrList)) {
        return Promise.reject(Error("Ошибка в формуле"));
      }
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(Error("Ошибка в формуле"));
    }
  },
});

export const getFormulaDepAttsIds = (
  attributes: ZAttribute[],
  attrTypesMap: Record<number, string>,
) => {
  const formulaAtts = attributes.filter(
    ({ valueType }) => attrTypesMap[valueType] === AttrTypeName.formula,
  );
  const formulaAttIds = formulaAtts.map(({ id }) => String(id));
  const depAtts = formulaAtts
    .map(({ viewStyles }) => {
      const editor = getEditorInfo(viewStyles);
      if (
        editor?.component?.editor === "Formula" ||
        editor?.component?.editor === "FormulaWithFinancial"
      ) {
        // В сохранённых argumentAtts могла потеряться одна из ссылок.
        // Читаем саму формулу, чтобы пересчёт работал без пересохранения.
        return getFormulaDependencyIds(editor.component.formula);
      }
      return [];
    })
    .flat();
  return {
    /**
     * formulaAttIds используются тут ПОТОМУ ЧТО И ФОРМУЛА тоже теперь может меняться (постановка от Макса...)
     * например, у нас есть FormulaWithFinancial, где мы можем менять целевую валюту
     *  */
    depAtts: [...depAtts, ...formulaAttIds],
    formulaAtts: formulaAttIds,
  };
};

const transformEdAttVals = (
  search: (attr: ZFormulaArg) => string,
  replace: (attr: ZFormulaArg) => string,
  formula: string,
  attrList: ZFormulaArg[],
) => {
  let newFormula = formula;
  attrList
    .slice()
    .sort((a, b) => b.name.length - a.name.length)
    .forEach((attr) => {
      newFormula = newFormula.replaceAll(search(attr), replace(attr));
    });
  return newFormula;
};

export const transformFormula4Save = (
  formula: string,
  attrList: ZFormulaArg[],
) =>
  transformEdAttVals(
    makeFormulaAttPartView,
    makeFormulaAttPartBuss,
    formula,
    attrList,
  );

export const transformFormula4Show = (
  formula: string,
  attrList: ZFormulaArg[],
) =>
  transformEdAttVals(
    makeFormulaAttPartBuss,
    makeFormulaAttPartView,
    formula,
    attrList,
  );
